import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { PrescriptionService } from "../../services/prescription.service";
import { LoaderService } from "../../services/loader.service";
import { Medicine } from 'src/interfaces/medicine';
import { DosageType } from 'src/interfaces/dosage-type';
import { MedicineDosagePackageType } from 'src/interfaces/medicine-dosage-package-type';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatSelectChange } from '@angular/material/select';
import { OrderItem } from 'src/interfaces/order-item';
import { AlertService } from 'src/core/services/alert.service';


@Component({
    selector: 'core-add-medicine',
    templateUrl: './add-medicine.component.html',
    styleUrls: ['./add-medicine.component.scss'],
    standalone: false
})
export class AddMedicineComponent implements OnInit {

  @Output() onMedicineAdd = new EventEmitter<OrderItem>();

  addMedicineForm: UntypedFormGroup = this._formBuilder.group({
    medicineFilter: ['', Validators.required],
    medicine: ['', Validators.required],
    dosageType: [{ value: '', disabled: true }, Validators.required],
    packageType: [{ value: '', disabled: true }, Validators.required],
    quantity: [{ value: '', disabled: true }, Validators.required]
  });

  medicine: Medicine[] = [];
  medicinePage: number = 1;
  medicineLimit: number = 30;
  dosageTypes: DosageType[] = [];
  medicineDosageTypePackageTypes: MedicineDosagePackageType[] = [];
  filteredMedicine: Observable<Medicine[]>;
  selectedMedicine: Medicine | null = null;
  selectedDosageType: DosageType | null = null;
  selectedMedicineDosagePackageType: MedicineDosagePackageType | null = null;

  isLoading: boolean = false;
  constructor(
    private _formBuilder: UntypedFormBuilder,
    private prescriptionService: PrescriptionService,
    private loaderService: LoaderService,
    private _alertService: AlertService
  ) { }

  ngOnInit(): void {

    this
      .loaderService
      .isLoading
      .subscribe(isLoading => {
        this.isLoading = isLoading;
      });

    this
      .addMedicineForm
      .controls['medicineFilter']
      .valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged()
      )
      .subscribe(value => {
        this.getMedicine(value);
      });
  }

  async getMedicine(value: string) {
    this.medicine = await this
      .prescriptionService
      .getMedicine(value, this.medicinePage, this.medicineLimit);
  }

  medicineSelected(event: MatAutocompleteSelectedEvent) {
    this.selectedMedicine = event.option.value;

    if (!this.selectedMedicine) return;

    this.addMedicineForm.patchValue({
      medicineFilter: this.selectedMedicine.name,
      medicine: this.selectedMedicine,
      dosageType: null,
      packageType: null
    });
    this.getDosageTypes(this.selectedMedicine.id);
  }

  getDosageTypes(medicineId: number) {
    this
      .prescriptionService
      .getMedicineDosageTypes(medicineId)
      .subscribe(dosageTypes => {
        
        this.dosageTypes = dosageTypes;
        if(this.dosageTypes.length  === 0){
          this._alertService.errorAlert("This medicine does not have any dosage types");
        } else {
          this.addMedicineForm.controls['dosageType'].enable();
        }
      });
  }

  getMedicineDosagePackageTypes(event: MatSelectChange) {
    const dosageType: DosageType = event.value;

    this.selectedDosageType = dosageType;
    if (!this.selectedDosageType || !this.selectedMedicine) return;

    this
      .prescriptionService
      .getMedicineDosagePackageTypes(this.selectedMedicine.id, this.selectedDosageType.id)
      .subscribe(medicineDosagePackageTypes => {
        this.medicineDosageTypePackageTypes = medicineDosagePackageTypes;
        if (this.medicineDosageTypePackageTypes.length === 0) {
          this._alertService.errorAlert("This medicine does not have any package types");
        } else {
          if (this.medicineDosageTypePackageTypes[0]?.expiry_date && new Date(this.medicineDosageTypePackageTypes[0]?.expiry_date) < new Date()) {
            this._alertService.warningAlert(`This medicine has passed the expiry date of ${new Date(this.medicineDosageTypePackageTypes[0]?.expiry_date).toISOString().split('T')[0]}`);
          } 
          this.addMedicineForm.controls['packageType'].enable();
        }
        
        
      })
  }

  packageTypeSelected(event: MatSelectChange) {

    const medicineDosagePackageType: MedicineDosagePackageType = event.value;

    if (medicineDosagePackageType) {
      this.selectedMedicineDosagePackageType = medicineDosagePackageType;
      this.addMedicineForm.controls['quantity'].enable();
    }
  }

  addOrderItem() {

    if (!this.addMedicineForm.valid) return;

    const values = {
      ...this.addMedicineForm.value
    }

    const medicine: Medicine = values.medicine;

    const medicineDosagePackageType: MedicineDosagePackageType = values.packageType;

    const dosageType: DosageType = values.dosageType;

    this.onMedicineAdd.emit({
      id: null,
      medicineDosagePackageType,
      dosageType,
      medicine,
      status: null,
      quantity: values.quantity,
      medicine_dosage_package_type_id: null,
      quantity_fulfilled: 0,
      deleted_at: undefined,
    });

    this.reset();
  }

  reset() {
    Object
      .keys(this.addMedicineForm.controls)
      .forEach((key) => {
        const control = this.addMedicineForm.controls[key];
        control.markAsPristine();
        control.markAsTouched();
      })
    this.addMedicineForm.reset();
    this.selectedMedicine = null;
    this.selectedDosageType = null;
    this.dosageTypes = [];
    this.medicineDosageTypePackageTypes = [];

    //  disable controls
    this.addMedicineForm.controls['dosageType'].disable();
    this.addMedicineForm.controls['packageType'].disable();
    this.addMedicineForm.controls['quantity'].disable();
  }

}
